import { reconstructInventoryExercise } from '../practice/inventoryPracticeSteps';
import type { OnboardingStep, OnboardingTour } from '../types';
import {
  cancelInventoryActions,
  applyLeakageAltPair,
  applyWalkthroughCount,
  closeAddWalkthroughProduct,
  confirmRelinkMatch,
  confirmSetupSave,
  continueSetupTarget,
  expandSectionViewAll,
  fillAddWalkthroughCount,
  filterLeakageNegative,
  openLeakageAddLink,
  openLeakageDates,
  openLeakageProduct,
  openRelinkUnlinked,
  openSectionAddProduct,
  openSetupNotSetup,
  openWalkthroughCountEdit,
  openWalkthroughGroup,
  searchLeakageSetupDish,
  selectLeakageSetupDish,
  continueLeakageSetup,
  showLeakageLinkedDish,
  openLeakageDishEdit,
  fillLeakageIngredientQty,
  showLeakageCharts,
  showLeakageSectionChart,
  searchRelinkMatch,
  selectAddWalkthroughProduct,
  selectRelinkMatch,
  selectSetupTarget,
  showAddedWalkthroughProduct,
  showAllInventories,
  showAttentionNotLinked,
  showAttentionNotSetup,
  showCountReviewTab,
  showInventoryCategories,
  showLeakageOverview,
  showLeakagePairPicker,
  showLeakageSetupRevenue,
  showLeakageTab,
  showLeakageTopLoss,
  showProductCategories,
  showRelinkOrderProducts,
  showSectionProductsPreview,
  showSectionQuantityMode,
  showSetupSubrecipes,
  startInventoryWalkthrough,
  switchLeakageStdUnits,
} from '../inventoryActions';
import { firstNeedsReviewProduct, firstUnlinkedProduct, getInventoryTourValues, leakageHasNegativeRows, leakageHasSection, leakageWalkthroughRowUid, notSetupProducts } from '../inventoryValues';
import { isCustomerInventoryPractice, setInventoryExerciseSource, useInventoryViewStore } from '../practice/inventoryPractice';

const skip = 'onboarding.skip';
const next = 'onboarding.next';
const route = '/inventory?tab=review';

export const inventorySteps: OnboardingStep[] = [
  {
    id: 'inventory-welcome',
    route,
    layout: 'hero',
    illustration: 'welcomeInventory',
    titleKey: 'onboarding.inventory.welcome.title',
    descriptionKey: 'onboarding.inventory.welcome.description',
    primaryLabelKey: 'onboarding.inventory.welcome.cta',
    secondaryLabelKey: skip,
    onEnter: startInventoryWalkthrough,
  },
  {
    id: 'inventory-count-tells',
    route,
    target: 'inventory-kpis',
    layout: 'spotlight',
    illustration: 'inventoryCountBox',
    titleKey: 'onboarding.inventory.countTells.title',
    descriptionKey: 'onboarding.inventory.countTells.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    separateHoles: true,
    padding: 4,
    radius: 12,
    onEnter: showCountReviewTab,
  },
  {
    id: 'inventory-divided',
    route,
    target: 'inventory-value',
    arrowTarget: 'inventory-value-title',
    layout: 'compact',
    illustration: 'windows',
    titleKey: 'onboarding.inventory.divided.title',
    descriptionKey: 'onboarding.inventory.divided.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'right',
    padding: 8,
    radius: 12,
    onEnter: showInventoryCategories,
  },
  {
    id: 'inventory-made-of',
    route,
    target: 'inventory-categories',
    layout: 'compact',
    illustration: 'inventoryPie',
    titleKey: 'onboarding.inventory.madeOf.title',
    descriptionKey: 'onboarding.inventory.madeOf.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 8,
    radius: 12,
    onEnter: showInventoryCategories,
  },
  {
    id: 'inventory-switch-perspective',
    route,
    target: 'inventory-categories',
    arrowTarget: 'inventory-cat-mode-product',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.switchPerspective.title',
    descriptionKey: 'onboarding.inventory.switchPerspective.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showInventoryCategories,
  },
  {
    id: 'inventory-product-split',
    route,
    target: 'inventory-categories',
    layout: 'compact',
    illustration: 'inventoryPie',
    titleKey: 'onboarding.inventory.productSplit.title',
    descriptionKey: 'onboarding.inventory.productSplit.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 8,
    radius: 12,
    onEnter: showProductCategories,
  },
  {
    id: 'inventory-needs-connecting',
    route,
    target: 'inventory-attention',
    layout: 'compact',
    illustration: 'link',
    titleKey: 'onboarding.inventory.needsConnecting.title',
    descriptionKey: 'onboarding.inventory.needsConnecting.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: showAttentionNotLinked,
  },
  {
    id: 'inventory-connect-product',
    route,
    target: 'inventory-attention',
    arrowTarget: 'inventory-attention-link',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.connectProduct.title',
    descriptionKey: 'onboarding.inventory.connectProduct.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showAttentionNotLinked,
  },
  {
    id: 'inventory-relink-intro',
    route,
    target: 'inventory-relink',
    layout: 'compact',
    illustration: 'link',
    titleKey: 'onboarding.inventory.relinkIntro.title',
    descriptionKey: 'onboarding.inventory.relinkIntro.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openRelinkUnlinked,
  },
  {
    id: 'inventory-relink-orders',
    route,
    target: 'inventory-relink',
    arrowTarget: 'inventory-relink-tab-products',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.relinkOrders.title',
    descriptionKey: 'onboarding.inventory.relinkOrders.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: openRelinkUnlinked,
  },
  {
    id: 'inventory-relink-search',
    route,
    target: 'inventory-relink',
    arrowTarget: 'inventory-relink-search',
    layout: 'compact',
    illustration: 'jumpToItem',
    titleKey: 'onboarding.inventory.relinkSearch.title',
    descriptionKey: 'onboarding.inventory.relinkSearch.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 0,
    radius: 10,
    onEnter: showRelinkOrderProducts,
  },
  {
    id: 'inventory-relink-pick',
    route,
    target: 'inventory-relink',
    arrowTarget: 'inventory-relink-pick',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.relinkPick.title',
    descriptionKey: 'onboarding.inventory.relinkPick.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: searchRelinkMatch,
  },
  {
    id: 'inventory-relink-confirm',
    route,
    target: 'inventory-relink',
    arrowTarget: 'inventory-relink-confirm',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.relinkConfirm.title',
    descriptionKey: 'onboarding.inventory.relinkConfirm.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: selectRelinkMatch,
  },
  {
    id: 'inventory-relink-clear',
    route,
    target: 'inventory-attention',
    layout: 'compact',
    illustration: 'check',
    titleKey: 'onboarding.inventory.relinkClear.title',
    descriptionKey: 'onboarding.inventory.relinkClear.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: confirmRelinkMatch,
  },
  {
    id: 'inventory-check-not-setup',
    route,
    target: 'inventory-attention',
    arrowTarget: 'inventory-att-notSetup',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.checkNotSetup.title',
    descriptionKey: 'onboarding.inventory.checkNotSetup.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showAttentionNotLinked,
  },
  {
    id: 'inventory-not-setup-list',
    route,
    target: 'inventory-attention',
    layout: 'compact',
    illustration: 'settings2',
    titleKey: 'onboarding.inventory.notSetupList.title',
    descriptionKey: 'onboarding.inventory.notSetupList.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: showAttentionNotSetup,
  },
  {
    id: 'inventory-setup-product',
    route,
    target: 'inventory-attention',
    arrowTarget: 'inventory-attention-setup',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.setupProduct.title',
    descriptionKey: 'onboarding.inventory.setupProduct.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showAttentionNotSetup,
  },
  {
    id: 'inventory-setup-path',
    route,
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-tab-subrecipe',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.setupPath.title',
    descriptionKey: 'onboarding.inventory.setupPath.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: openSetupNotSetup,
  },
  {
    id: 'inventory-setup-pick',
    route,
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-pick',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.setupPick.title',
    descriptionKey: 'onboarding.inventory.setupPick.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: showSetupSubrecipes,
  },
  {
    id: 'inventory-setup-continue',
    route,
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-continue',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.setupContinue.title',
    descriptionKey: 'onboarding.inventory.setupContinue.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: selectSetupTarget,
  },
  {
    id: 'inventory-setup-save',
    route,
    target: 'inventory-setup',
    arrowTarget: 'cost-subrecipe-save',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.setupSave.title',
    descriptionKey: 'onboarding.inventory.setupSave.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: continueSetupTarget,
  },
  {
    id: 'inventory-setup-clear',
    route,
    target: 'inventory-attention',
    layout: 'compact',
    illustration: 'check',
    titleKey: 'onboarding.inventory.setupClear.title',
    descriptionKey: 'onboarding.inventory.setupClear.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: confirmSetupSave,
  },
  {
    id: 'inventory-jump-inside',
    route,
    target: 'inventory-value',
    arrowTarget: 'inventory-value-drill',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.jumpInside.title',
    descriptionKey: 'onboarding.inventory.jumpInside.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'right',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showAllInventories,
  },
  {
    id: 'inventory-drill-sections',
    route,
    target: 'inventory-value',
    layout: 'compact',
    illustration: 'inventoryAb',
    titleKey: 'onboarding.inventory.drillSections.title',
    descriptionKey: 'onboarding.inventory.drillSections.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'right',
    padding: 8,
    radius: 12,
    onEnter: openWalkthroughGroup,
  },
  {
    id: 'inventory-step-inside-section',
    route,
    target: 'inventory-value',
    arrowTarget: 'inventory-value-section',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.stepInsideSection.title',
    descriptionKey: 'onboarding.inventory.stepInsideSection.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'right',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: openWalkthroughGroup,
  },
  {
    id: 'inventory-section-makeup',
    target: 'inventory-section-products',
    layout: 'compact',
    illustration: 'inventoryClipboard',
    titleKey: 'onboarding.inventory.sectionMakeup.title',
    descriptionKey: 'onboarding.inventory.sectionMakeup.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: showSectionProductsPreview,
  },
  {
    id: 'inventory-section-view-all',
    target: 'inventory-section-products',
    arrowTarget: 'inventory-section-view-all',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.sectionViewAll.title',
    descriptionKey: 'onboarding.inventory.sectionViewAll.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showSectionProductsPreview,
  },
  {
    id: 'inventory-section-count-worth',
    target: 'inventory-section-products',
    layout: 'compact',
    illustration: 'inventoryClipboard',
    titleKey: 'onboarding.inventory.sectionCountWorth.title',
    descriptionKey: 'onboarding.inventory.sectionCountWorth.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: expandSectionViewAll,
  },
  {
    id: 'inventory-section-correct-count',
    target: 'inventory-section-products',
    arrowTarget: 'inventory-section-count',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.sectionCorrectCount.title',
    descriptionKey: 'onboarding.inventory.sectionCorrectCount.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: expandSectionViewAll,
  },
  {
    id: 'inventory-section-change-count',
    target: 'inventory-section-products',
    arrowTarget: 'inventory-section-count',
    layout: 'compact',
    illustration: 'pencil',
    titleKey: 'onboarding.inventory.sectionChangeCount.title',
    descriptionKey: 'onboarding.inventory.sectionChangeCount.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 8,
    radius: 12,
    onEnter: openWalkthroughCountEdit,
  },
  {
    id: 'inventory-section-count-applied',
    target: 'inventory-section-products',
    emphasisTarget: 'inventory-section-product',
    layout: 'compact',
    illustration: 'check',
    titleKey: 'onboarding.inventory.sectionCountApplied.title',
    descriptionKey: 'onboarding.inventory.sectionCountApplied.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: applyWalkthroughCount,
  },
  {
    id: 'inventory-section-see-quantity',
    target: 'inventory-section-products',
    arrowTarget: 'inventory-section-mode-quantity',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.sectionSeeQuantity.title',
    descriptionKey: 'onboarding.inventory.sectionSeeQuantity.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: expandSectionViewAll,
  },
  {
    id: 'inventory-section-verify-stock',
    target: 'inventory-section-products',
    layout: 'compact',
    illustration: 'inventoryScale',
    titleKey: 'onboarding.inventory.sectionVerifyStock.title',
    descriptionKey: 'onboarding.inventory.sectionVerifyStock.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: showSectionQuantityMode,
  },
  {
    id: 'inventory-section-add-product',
    target: 'inventory-section-products',
    arrowTarget: 'inventory-section-add',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.sectionAddProduct.title',
    descriptionKey: 'onboarding.inventory.sectionAddProduct.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showSectionQuantityMode,
  },
  {
    id: 'inventory-add-ready',
    target: 'inventory-add-product',
    arrowTarget: 'inventory-add-product-list',
    layout: 'compact',
    illustration: 'inventoryClipboard',
    titleKey: 'onboarding.inventory.addReady.title',
    descriptionKey: 'onboarding.inventory.addReady.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openSectionAddProduct,
  },
  {
    id: 'inventory-add-pick',
    target: 'inventory-add-product',
    arrowTarget: 'inventory-add-product-pick',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.addPick.title',
    descriptionKey: 'onboarding.inventory.addPick.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: openSectionAddProduct,
  },
  {
    id: 'inventory-add-count',
    target: 'inventory-add-product',
    arrowTarget: 'inventory-add-product-count',
    layout: 'compact',
    illustration: 'pencil',
    titleKey: 'onboarding.inventory.addCount.title',
    descriptionKey: 'onboarding.inventory.addCount.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 0,
    radius: 12,
    onEnter: selectAddWalkthroughProduct,
  },
  {
    id: 'inventory-add-confirm',
    target: 'inventory-add-product',
    arrowTarget: 'inventory-add-product-confirm',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.addConfirm.title',
    descriptionKey: 'onboarding.inventory.addConfirm.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: fillAddWalkthroughCount,
  },
  {
    id: 'inventory-add-done',
    target: 'inventory-section-products',
    arrowTarget: 'inventory-section-added',
    layout: 'compact',
    illustration: 'check',
    titleKey: 'onboarding.inventory.addDone.title',
    descriptionKey: 'onboarding.inventory.addDone.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    padding: 8,
    radius: 12,
    onEnter: showAddedWalkthroughProduct,
  },
  {
    id: 'inventory-complete',
    layout: 'complete',
    illustration: 'completeInventory',
    titleKey: 'onboarding.inventory.complete.title',
    descriptionKey: 'onboarding.inventory.complete.description',
    primaryLabelKey: 'onboarding.goToInventory',
    secondaryLabelKey: 'onboarding.restart',
    secondaryAction: 'restart',
    primaryRoute: route,
    onEnter: closeAddWalkthroughProduct,
  },
  {
    id: 'inventory-leakage-welcome',
    route,
    layout: 'hero',
    illustration: 'welcomeLeakage',
    titleKey: 'onboarding.inventory.leakageWelcome.title',
    descriptionKey: 'onboarding.inventory.leakageWelcome.description',
    primaryLabelKey: 'onboarding.inventory.leakageWelcome.cta',
    secondaryLabelKey: skip,
    onEnter: showLeakageTab,
  },
  {
    id: 'inventory-leakage-kpis',
    route,
    target: 'inventory-leakage-kpis',
    arrowTarget: 'inventory-leakage-kpi-leakage',
    layout: 'spotlight',
    illustration: 'leakageJars',
    titleKey: 'onboarding.inventory.leakageKpis.title',
    descriptionKey: 'onboarding.inventory.leakageKpis.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    separateHoles: true,
    padding: 4,
    radius: 12,
    onEnter: showLeakageOverview,
  },
  {
    id: 'inventory-leakage-dates',
    route,
    target: 'inventory-leakage-dates-btn',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageDates.title',
    descriptionKey: 'onboarding.inventory.leakageDates.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: openLeakageDates,
  },
  {
    id: 'inventory-leakage-pair',
    route,
    target: 'inventory-leakage-sessions',
    arrowTarget: 'inventory-leakage-apply',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakagePair.title',
    descriptionKey: 'onboarding.inventory.leakagePair.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showLeakagePairPicker,
  },
  {
    id: 'inventory-leakage-new-story',
    route,
    target: 'inventory-leakage-kpis',
    arrowTarget: 'inventory-leakage-kpi-expected',
    layout: 'compact',
    illustration: 'inventoryScale',
    titleKey: 'onboarding.inventory.leakageNewStory.title',
    descriptionKey: 'onboarding.inventory.leakageNewStory.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    separateHoles: true,
    padding: 4,
    radius: 12,
    onEnter: applyLeakageAltPair,
  },
  {
    id: 'inventory-leakage-top-loss',
    route,
    target: 'inventory-leakage-table',
    arrowTarget: 'inventory-leakage-row',
    layout: 'compact',
    illustration: 'dropping',
    titleKey: 'onboarding.inventory.leakageTopLoss.title',
    descriptionKey: 'onboarding.inventory.leakageTopLoss.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: showLeakageTopLoss,
  },
  {
    id: 'inventory-leakage-filter',
    route,
    target: 'inventory-leakage-dir-negative',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageFilter.title',
    descriptionKey: 'onboarding.inventory.leakageFilter.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showLeakageTopLoss,
  },
  {
    id: 'inventory-leakage-drivers',
    route,
    target: 'inventory-leakage-table',
    layout: 'compact',
    illustration: 'dropping',
    titleKey: 'onboarding.inventory.leakageDrivers.title',
    descriptionKey: 'onboarding.inventory.leakageDrivers.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: filterLeakageNegative,
  },
  {
    id: 'inventory-leakage-units',
    route,
    target: 'inventory-leakage-unit-std',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageUnits.title',
    descriptionKey: 'onboarding.inventory.leakageUnits.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: filterLeakageNegative,
  },
  {
    id: 'inventory-leakage-converted',
    route,
    target: 'inventory-leakage-table',
    arrowTarget: 'inventory-leakage-kpi-leakage',
    layout: 'compact',
    illustration: 'filteredView',
    titleKey: 'onboarding.inventory.leakageConverted.title',
    descriptionKey: 'onboarding.inventory.leakageConverted.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: switchLeakageStdUnits,
  },
  {
    id: 'inventory-leakage-open-row',
    route,
    target: 'inventory-leakage-row',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageOpenRow.title',
    descriptionKey: 'onboarding.inventory.leakageOpenRow.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: switchLeakageStdUnits,
  },
  {
    id: 'inventory-leakage-calc',
    target: 'inventory-leakage-drawer',
    arrowTarget: 'inventory-leakage-calc',
    layout: 'compact',
    illustration: 'costCalculator',
    titleKey: 'onboarding.inventory.leakageCalc.title',
    descriptionKey: 'onboarding.inventory.leakageCalc.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openLeakageProduct,
  },
  {
    id: 'inventory-leakage-section',
    target: 'inventory-leakage-drawer',
    arrowTarget: 'inventory-leakage-section',
    layout: 'compact',
    illustration: 'inventoryColors',
    titleKey: 'onboarding.inventory.leakageSection.title',
    descriptionKey: 'onboarding.inventory.leakageSection.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openLeakageProduct,
  },
  {
    id: 'inventory-leakage-usage',
    target: 'inventory-leakage-drawer',
    arrowTarget: 'inventory-leakage-usage',
    layout: 'compact',
    illustration: 'costCalculator',
    titleKey: 'onboarding.inventory.leakageUsage.title',
    descriptionKey: 'onboarding.inventory.leakageUsage.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openLeakageProduct,
  },
  {
    id: 'inventory-leakage-add-link',
    target: 'inventory-leakage-drawer',
    arrowTarget: 'inventory-leakage-add-link',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageAddLink.title',
    descriptionKey: 'onboarding.inventory.leakageAddLink.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: openLeakageProduct,
  },
  {
    id: 'inventory-leakage-setup-type',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-tab-revenue',
    layout: 'compact',
    illustration: 'basket',
    titleKey: 'onboarding.inventory.leakageSetupType.title',
    descriptionKey: 'onboarding.inventory.leakageSetupType.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openLeakageAddLink,
  },
  {
    id: 'inventory-leakage-setup-search',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-search',
    layout: 'compact',
    illustration: 'costFind',
    titleKey: 'onboarding.inventory.leakageSetupSearch.title',
    descriptionKey: 'onboarding.inventory.leakageSetupSearch.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: showLeakageSetupRevenue,
  },
  {
    id: 'inventory-leakage-setup-pick',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-pick',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageSetupPick.title',
    descriptionKey: 'onboarding.inventory.leakageSetupPick.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: searchLeakageSetupDish,
  },
  {
    id: 'inventory-leakage-setup-continue',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-continue',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageSetupContinue.title',
    descriptionKey: 'onboarding.inventory.leakageSetupContinue.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: selectLeakageSetupDish,
  },
  {
    id: 'inventory-leakage-setup-done',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-done',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageSetupDone.title',
    descriptionKey: 'onboarding.inventory.leakageSetupDone.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: continueLeakageSetup,
  },
  {
    id: 'inventory-leakage-edit-dish',
    target: 'inventory-leakage-drawer',
    arrowTarget: 'inventory-leakage-edit-dish',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.inventory.leakageEditDish.title',
    descriptionKey: 'onboarding.inventory.leakageEditDish.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: showLeakageLinkedDish,
  },
  {
    id: 'inventory-leakage-edit-qty',
    target: 'cost-detail-panel',
    arrowTarget: 'inventory-leakage-ing-qty',
    layout: 'compact',
    illustration: 'pencil',
    titleKey: 'onboarding.inventory.leakageEditQty.title',
    descriptionKey: 'onboarding.inventory.leakageEditQty.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 0,
    radius: 12,
    onEnter: openLeakageDishEdit,
  },
  {
    id: 'inventory-leakage-edit-back',
    target: 'cost-detail-panel',
    arrowTarget: 'cost-detail-close',
    layout: 'compact',
    illustration: 'reverseArrow',
    titleKey: 'onboarding.inventory.leakageEditBack.title',
    descriptionKey: 'onboarding.inventory.leakageEditBack.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: fillLeakageIngredientQty,
  },
  {
    id: 'inventory-leakage-categories',
    route,
    target: 'inventory-leakage-categories',
    layout: 'compact',
    illustration: 'inventoryBars',
    titleKey: 'onboarding.inventory.leakageCategories.title',
    descriptionKey: 'onboarding.inventory.leakageCategories.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    waitForTarget: true,
    padding: 8,
    radius: 16,
    onEnter: showLeakageCharts,
  },
  {
    id: 'inventory-leakage-sections',
    route,
    target: 'inventory-leakage-sections',
    layout: 'compact',
    illustration: 'dropping',
    titleKey: 'onboarding.inventory.leakageSections.title',
    descriptionKey: 'onboarding.inventory.leakageSections.description',
    values: getInventoryTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    waitForTarget: true,
    padding: 8,
    radius: 16,
    onEnter: showLeakageSectionChart,
  },
  {
    id: 'inventory-leakage-complete',
    layout: 'complete',
    illustration: 'completeLeakage',
    titleKey: 'onboarding.inventory.leakageComplete.title',
    descriptionKey: 'onboarding.inventory.leakageComplete.description',
    primaryLabelKey: 'onboarding.goToLeakage',
    secondaryLabelKey: 'onboarding.restart',
    secondaryAction: 'restart',
    primaryRoute: route,
    onEnter: showLeakageCharts,
  },
];


const inventoryOnly = inventorySteps.filter(step => !step.id.startsWith('inventory-leakage-'));
const connectedEnd = inventoryOnly.find(step => step.id === 'inventory-complete');
if (connectedEnd) Object.assign(connectedEnd, { primaryTour: 'leakage', primaryRoute: '/inventory?tab=variance', primaryLabelKey: 'onboarding.goToLeakage', secondaryLabelKey: 'onboarding.done', secondaryAction: 'complete' });
let previousInventoryStep = -1;
let previousLeakageStep = -1;
let sourceTransitionPending = false;
export function prepareInventoryTourStep(stepId: string) {
  const mode = stepId.startsWith('inventory-leakage-') ? 'leakage' : 'inventory';
  const steps = mode === 'inventory' ? inventoryOnly : inventorySteps.filter(step => step.id.startsWith('inventory-leakage-'));
  const index = steps.findIndex(step => step.id === stepId);
  const boundary = steps.findIndex(step => step.id === (mode === 'inventory' ? 'inventory-connect-product' : 'inventory-leakage-add-link'));
  if (index >= 0) {
    const wasCustomer = isCustomerInventoryPractice();
    setInventoryExerciseSource(mode, index >= boundary);
    sourceTransitionPending = sourceTransitionPending || wasCustomer !== isCustomerInventoryPractice();
  }
}
function availableForCustomer(step: OnboardingStep, mode: 'inventory' | 'leakage') {
  if (!isCustomerInventoryPractice()) return step.isAvailable?.() ?? true;
  // Switching comparisons would require replacing the frozen server review. The
  // current dates remain explained by the live KPI step; exercises start below.
  if (['inventory-leakage-dates', 'inventory-leakage-pair', 'inventory-leakage-new-story'].includes(step.id)) return false;
  const data = useInventoryViewStore.getState().countReview.data;
  if (step.id === 'inventory-divided') return !!data?.groups.some(group => group.groupName);
  if (step.id === 'inventory-needs-connecting') return !!firstUnlinkedProduct();
  if (step.id === 'inventory-made-of') return !!data?.categoriesInventory.length;
  if (step.id === 'inventory-switch-perspective' || step.id === 'inventory-product-split') return !!data?.categoriesProduct.length;
  if (step.id === 'inventory-check-not-setup' || step.id === 'inventory-not-setup-list') return notSetupProducts().length > 0;
  if (step.id === 'inventory-check-needs-review' || step.id === 'inventory-review-standout') return !!firstNeedsReviewProduct();
  if (step.id === 'inventory-leakage-filter' || step.id === 'inventory-leakage-drivers') return leakageHasNegativeRows();
  if (step.id === 'inventory-leakage-section') return leakageHasSection();
  if (mode === 'leakage' && ['inventory-leakage-top-loss', 'inventory-leakage-units', 'inventory-leakage-converted', 'inventory-leakage-open-row', 'inventory-leakage-calc', 'inventory-leakage-usage'].includes(step.id)) return !!leakageWalkthroughRowUid();
  return true;
}
function enterStep(step: OnboardingStep, index: number, mode: 'inventory' | 'leakage', ids: string[]) {
  cancelInventoryActions();
  const wasCustomer = isCustomerInventoryPractice();
  const exerciseIndex = ids.indexOf(mode === 'inventory' ? 'inventory-connect-product' : 'inventory-leakage-add-link');
  setInventoryExerciseSource(mode, index >= exerciseIndex);
  const changedSource = sourceTransitionPending || wasCustomer !== isCustomerInventoryPractice();
  sourceTransitionPending = false;
  const previous = mode === 'inventory' ? previousInventoryStep : previousLeakageStep;
  if (index === 0) startInventoryWalkthrough();
  else if (index < previous && !isCustomerInventoryPractice()) reconstructInventoryExercise(mode, step.id, ids);
  if (mode === 'inventory') previousInventoryStep = index;
  else previousLeakageStep = index;
  // Reconstructed fixtures must render before actions inspect their drawer targets.
  if (changedSource || (index > 0 && index < previous)) {
    const timer = window.setTimeout(() => step.onEnter?.(), 0);
    return () => { window.clearTimeout(timer); cancelInventoryActions(); };
  }
  step.onEnter?.();
  return cancelInventoryActions;
}
export const inventoryTour: OnboardingTour = { id: 'inventory', paths: ['/inventory'], entryRoute: '/inventory?tab=review', ownedRoutePrefixes: ['/inventory/section/', '/inventory/all-products'],
  steps: inventoryOnly.map((step, index) => ({...step, values: step.values ?? getInventoryTourValues, isAvailable: () => availableForCustomer(step, 'inventory'), route: step.id.startsWith('inventory-section-') || step.id.startsWith('inventory-add-') ? '/inventory/section/onboarding-practice-inventory-kitchen?groupId=onboarding-practice-inventory-group' : '/inventory?tab=review', practice: 'inventory', onEnter: () => enterStep(step, index, 'inventory', inventoryOnly.map(item => item.id)) })) };
export const leakageTour: OnboardingTour = { id: 'leakage', paths: ['/inventory'], entryRoute: '/inventory?tab=variance',
  steps: inventorySteps.filter(step => step.id.startsWith('inventory-leakage-')).map((step, index) => ({...step, values: step.values ?? getInventoryTourValues, isAvailable: () => availableForCustomer(step, 'leakage'), practice: 'leakage', route: step.route ? '/inventory?tab=variance' : undefined,
    primaryRoute: step.primaryRoute ? '/inventory?tab=variance' : undefined,
    onEnter: () => enterStep(step, index, 'leakage', inventorySteps.filter(item => item.id.startsWith('inventory-leakage-')).map(item => item.id)) })) };
